


























































































import { Component, Vue, Prop, Watch, Inject } from "vue-property-decorator";
import { DataPoint, HSL, Name, NameData } from "../models";
import { scaleLinear } from "d3-scale";
import { select } from "d3-selection";
import { axisBottom, axisLeft } from "d3-axis";
import { path } from "d3-path";
import { stack, stackOffsetNone, stackOrderNone } from "d3-shape";
import { area as d3area } from "d3-shape";
import { paletteGenerator } from "../utils/paletteGenerator";

@Component({
  mounted() {
    if (!this.$el) return;
    const { width, height } = this.$el.getBoundingClientRect();
    this.$data.computedHeight = this.$data.defaultHeight || height || 300;
    this.$data.svgWidth = width || 300;
  },
})
export default class NameStreamGraph extends Vue {
  @Prop({ default: [] }) readonly nameData!: [Name, DataPoint[]][];
  @Prop({ default: 2010 }) minYear!: number;
  @Prop({ default: 2015 }) maxYear!: number;
  @Prop({ default: false }) noTooltip: boolean;

  @Prop({ default: undefined }) defaultHeight: undefined | number;

  @Prop({ default: () => [] }) xTicks!: number[];
  @Prop({ default: () => [] }) yTicks!: number[];
  @Prop({ default: false }) hideXAxis: boolean;
  @Prop({ default: false }) horizontalXTicks: boolean;

  bottomMargin = 20;

  computedHeight = 300;

  svgWidth = 300;
  get svgHeight() {
    return this.defaultHeight || this.computedHeight;
  }

  getContainerRect() {
    const { container } = this.$refs;
    if (!container) return null;
    return (container as Element).getBoundingClientRect();
  }

  @Inject("primaryColour") private primaryColour!: HSL;

  get years(): number[] {
    const returnVal = [];
    for (let i = this.minYear; i <= this.maxYear; i++) {
      returnVal.push(i);
    }
    return returnVal;
  }

  get keys() {
    return this.nameData.map((record) => record[0]);
  }

  get d3Keys(): string[] {
    //
    return this.keys.map((key) => {
      return this.getKeyFromName(key);
    });
  }

  mousemove(event: MouseEvent) {
    const { clientX } = event;
    if (!this.$el) return;
    const svg = this.$el.querySelector("svg");
    const svgX = svg!.getBoundingClientRect().left;

    const internalX = clientX - svgX;
    const year = Math.round(this.invertX(internalX));
    this.tooltipYear = year;
  }

  getDataForD3(
    inputDatum: [Name, DataPoint[]]
  ): {
    [key: string]: number;
  }[] {
    const [name, data] = inputDatum;
    const key = this.getKeyFromName(name);

    const returnVal: {
      [key: string]: number;
    }[] = [];
    data.forEach((datum) => {
      const point: {
        [key: string]: number;
      } = {};
      point[key] = datum.count;
      returnVal.push(point);
    });

    return returnVal;
  }

  get dataForD3() {
    return this.nameData.map(this.getDataForD3);
  }

  getKeyFromName(inputName: Name): string {
    const { name, gender } = inputName;
    const unisex =
      this.nameData.map((record) => record[0]).filter((k) => k.name === name)
        .length > 1;
    return unisex ? `${name} (${gender})` : name;
  }

  get margin() {
    let left = this.yTicks.length > 0 ? 50 : 10;

    const top = this.yTicks.length > 0 ? 50 : 0;
    let right = 10;
    let bottom = 0;
    if (this.xTicks.length === 0) {
      bottom = 0;
    } else {
      if (this.horizontalXTicks) {
        bottom = 25;
        left = Math.max(left, 25);
        right = Math.max(right, 25);
      } else {
        bottom = 50;
      }
    }
    return { top, left, right, bottom };
  }

  tooltipText = "";
  tooltipYear = 2010;
  tooltipKey = "";
  get tooltipCount() {
    const thisYear = this.series.get(this.tooltipYear);
    return thisYear ? thisYear[this.tooltipKey] || 0 : 0;
  }

  activateItem(i: number) {
    const activeName = this.nameData[i][0];
    this.tooltipKey = this.getKeyFromName(activeName);
    this.tooltipText = `${activeName.name}`;
  }

  get series(): Map<
    number,
    {
      [key: string]: number;
    }
  > {
    const m = new Map<
      number,
      {
        [key: string]: number;
      }
    >();
    this.nameData.forEach((record) => {
      const name = record[0];
      const years = record[1];
      const key = this.getKeyFromName(name);
      for (let i = this.minYear; i <= this.maxYear; i++) {
        const year = i;
        const yearData = years.find((datapoint) => datapoint.year === year);
        const count = yearData ? yearData.count : 0;
        const currentYearData =
          m.get(year) ||
          ({} as {
            [key: string]: number;
          });
        currentYearData[key] = count;
        m.set(year, currentYearData);
      }
    });
    return m;
  }

  getColour(i: number) {
    const { hue, saturation, light } = this.primaryColour;
    return paletteGenerator(
      { hue, saturation: saturation - 30, light: light + 20 },
      i
    );
  }

  get maxGraphDomain(): number {
    const yearHeights: number[] = Array.from(this.series.values()).map(
      (val) => {
        const vals = Object.values(val);
        return vals.reduce((acc: number, cur: number) => acc + cur);
      }
    );
    return Math.max(...yearHeights);
  }

  getDataForName(name: Name) {
    return this.nameData.find((record) => record[0] === name)![1];
  }

  stackFn(data: any[]) {
    return stack().keys(this.d3Keys)(data);
  }
  get stacked() {
    return this.stackFn(Array.from(this.series.values()));
  }

  // convert point on x to year
  invertX(val: number) {
    const { left, right } = this.margin;
    return scaleLinear()
      .domain([left, this.svgWidth - right])
      .range([this.minYear, this.maxYear])(val);
  }

  // convert year to point on x axis
  x(val: number) {
    const { left, right } = this.margin;
    return scaleLinear()
      .domain([this.minYear, this.maxYear])
      .range([left, this.svgWidth - right])(val);
  }

  y(val: number) {
    return scaleLinear()
      .domain([0, this.maxGraphDomain])
      .range([this.svgHeight - this.margin.bottom, this.margin.top])(val);
  }

  area(val: [number, number][]) {
    const area = d3area()
      .x((d, i) => this.x(this.minYear + i))
      .y0((d) => this.y(d[0]))
      .y1((d) => this.y(d[1]));
    const path = area(val);
    return path;
  }
}
